var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var SegmentedControl_1;
import React, { Component, Fragment } from 'react';
import { ButtonTheme, Direction, TextAlignment } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType, Container } from '@philips-internal/oswald-ui-core';
import { arrayOf, bool, element, eventListener, number, object, oneOf, oneOfType, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ChangeEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { classNames, enumValues, isEnumValid, isFunction, isMobile, isValidWidth, propagateData } from '@philips-internal/oswald-ui-utils';
import { SegmentedControlAPI } from './segmented-control-api.js';
import { SegmentedControlViewMode } from './segmented-control-constants.js';
import './segmented-control.css';
const SegmentedControlContainer = {
    acceptedTypes: [
        ComponentType.BEHAVIOR,
        ComponentType.DATA_DISPLAY,
        ComponentType.LAYOUT,
    ],
    name: '',
};
let SegmentedControl = SegmentedControl_1 = class SegmentedControl extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            firstItemShown: 0,
            showArrows: false,
            shownItems: [],
        };
        this.onChange = (item = {}) => {
            const { atLeastOne, enabled, multiSelect, value: oldValue = [], onChange, readOnly } = this.props;
            if (readOnly || !enabled) {
                return;
            }
            const isItemAlreadySelected = oldValue.some(this.isEqual(item));
            if (!multiSelect && isItemAlreadySelected && atLeastOne)
                return;
            const value = this.getNewValue(item, oldValue, isItemAlreadySelected);
            isFunction(onChange) && onChange(new ChangeEvent({ oldValue, value }));
        };
    }
    componentDidUpdate(prevProps, prevState) {
        const { data, mode } = this.props;
        const { firstItemShown, showArrows } = this.state;
        const horizontalNoWrap = mode === SegmentedControlViewMode.HORIZONTAL_NO_WRAP;
        if (prevProps.data !== data || prevState.firstItemShown !== firstItemShown) {
            const lastItemShown = firstItemShown + this.itemsShown();
            const shownItems = horizontalNoWrap ? data.slice(firstItemShown, lastItemShown) : data;
            this.setState({ shownItems });
        }
        if (data.length > this.itemsShown() && horizontalNoWrap && !showArrows) {
            this.setState({ showArrows: true });
        }
    }
    itemsShown() {
        const { itemWidth } = this.props;
        return Math.max(1, Math.floor(12 / itemWidth));
    }
    getIdentifier(item) {
        const { identifier } = this.props;
        return String(item[identifier]);
    }
    isEqual(item) {
        return (selectedValue) => this.getIdentifier(selectedValue) === this.getIdentifier(item);
    }
    isDiff(item) {
        return (selectedValue) => this.getIdentifier(selectedValue) !== this.getIdentifier(item);
    }
    getNewValue(item, oldValue, isItemAlreadySelected) {
        const { multiSelect } = this.props;
        if (multiSelect) {
            if (isItemAlreadySelected) {
                return oldValue.filter(this.isDiff(item));
            }
            return [...oldValue, item];
        }
        if (!isItemAlreadySelected) {
            return [item];
        }
        return [];
    }
    isSelected(item) {
        const { value = [] } = this.props;
        return value.some(this.isEqual(item));
    }
    renderItem(item) {
        const { children, enabled = SegmentedControl_1.defaultProps.enabled, expandToFill, mode, itemWidth, readOnly, theme } = this.props;
        const isSelected = this.isSelected(item);
        const className = classNames({
            'c-segmented-control__component': true,
            'c-segmented-control__item-effect': !readOnly || enabled,
            'c-segmented-control--disabled': !enabled,
            'c-segmented-control--read-only': readOnly,
            'is-selected': isSelected,
            't-light': theme === ButtonTheme.ACCENT && isSelected,
        });
        const wrapperClassName = classNames({
            'c-segmented-control__item': mode === SegmentedControlViewMode.HORIZONTAL_WRAP,
            'c-segmented-control__item-no-wrap': mode === SegmentedControlViewMode.HORIZONTAL_NO_WRAP,
            'is-flex': expandToFill,
            [`u-col-${itemWidth}`]: isValidWidth(itemWidth),
        });
        const identifier = this.getIdentifier(item);
        return (React.createElement("div", { className: wrapperClassName, key: identifier },
            React.createElement("div", { className: className, onClick: () => this.onChange(item) }, propagateData(children, item, { identifier, key: identifier }, true))));
    }
    onNextPageClick() {
        this.setState((prevState) => ({ firstItemShown: prevState.firstItemShown + 1 }));
    }
    onPreviousPageClick() {
        this.setState((prevState) => ({ firstItemShown: prevState.firstItemShown - 1 }));
    }
    renderLeftArrow() {
        const { firstItemShown, showArrows } = this.state;
        if (!showArrows || firstItemShown === 0) {
            return null;
        }
        return (React.createElement("div", { className: `c-segmented-control__button c-segmented-control__button--left`, onClick: () => this.onPreviousPageClick() }));
    }
    renderRightArrow() {
        const { data } = this.props;
        const { firstItemShown, showArrows } = this.state;
        if (!showArrows || firstItemShown === (data.length - this.itemsShown())) {
            return null;
        }
        return (React.createElement("div", { className: `c-segmented-control__button c-segmented-control__button--right`, onClick: () => this.onNextPageClick() }));
    }
    renderDesktop() {
        const { mode } = this.props;
        const { shownItems } = this.state;
        const className = classNames({
            'c-segmented-control__no-wrap': mode === SegmentedControlViewMode.HORIZONTAL_NO_WRAP,
            'c-segmented-control__wrap': mode === SegmentedControlViewMode.HORIZONTAL_WRAP,
        });
        return (React.createElement(Fragment, null,
            this.renderLeftArrow(),
            React.createElement("div", { className: className }, shownItems.map((item) => this.renderItem(item))),
            this.renderRightArrow()));
    }
    render() {
        const { alignment, children, data, directionOnMobile, mode, theme, uuid } = this.props;
        const className = classNames({
            'c-segmented-control': true,
            [`c-segmented-control--${alignment}`]: isEnumValid(TextAlignment, alignment),
            [`c-segmented-control--${theme}`]: isEnumValid(ButtonTheme, theme),
            'c-segmented-control__no-wrap': mode === SegmentedControlViewMode.HORIZONTAL_NO_WRAP,
            'c-segmented-control__view-horizontal': isMobile() && directionOnMobile === Direction.HORIZONTAL,
        });
        if (!Array.isArray(data) || !data.length) {
            return (React.createElement(Container, { acceptedTypes: SegmentedControlContainer.acceptedTypes, className: className, repeated: true, uuid: uuid }, children));
        }
        return (React.createElement(Container, { acceptedTypes: SegmentedControlContainer.acceptedTypes, className: className, repeated: true, uuid: uuid }, isMobile()
            ? data.map((item) => this.renderItem(item))
            : this.renderDesktop()));
    }
};
SegmentedControl.propTypes = {
    alignment: oneOf(enumValues(TextAlignment)),
    atLeastOne: bool.group('behaviour'),
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    data: arrayOf(object).notEnumerable(),
    directionOnMobile: oneOf(enumValues(Direction)),
    enabled: bool,
    expandToFill: bool.group('behaviour'),
    identifier: string,
    itemWidth: number,
    mode: oneOf(enumValues(SegmentedControlViewMode)),
    multiSelect: bool.group('behaviour'),
    onChange: eventListener,
    readOnly: bool,
    theme: oneOf(enumValues(ButtonTheme)),
    uuid: string,
    value: arrayOf(object).notEnumerable(),
};
SegmentedControl.defaultProps = {
    alignment: TextAlignment.LEFT,
    atLeastOne: true,
    data: [],
    directionOnMobile: Direction.VERTICAL,
    enabled: true,
    expandToFill: true,
    itemWidth: 3,
    mode: SegmentedControlViewMode.HORIZONTAL_WRAP,
    multiSelect: false,
    readOnly: false,
};
SegmentedControl = SegmentedControl_1 = __decorate([
    AwesomeComponent({ api: SegmentedControlAPI, containers: [SegmentedControlContainer], type: ComponentType.DATA_ENTRY })
], SegmentedControl);
export { SegmentedControl, };
//# sourceMappingURL=segmented-control.js.map